import {
  Accordion,
  AccordionSection,
  ActionMenu,
  ActionMenuItem,
  ActionMenuItemSpacer,
  Heading,
  Paragraph,
  VisualSizesEnum,
} from '@frontapp/ui-kit';
import { addSeconds, formatDistanceToNowStrict } from 'date-fns';
import { useNotifications } from 'reapop';
import Organizations from './Organizations';
import Sites from './Sites';
import CopyActionMenuItem from './menuItems/CopyActionMenuItem';
import GotoActionMenuItem from './menuItems/GotoActionMenuItem';
import { type Account, levelEmoji } from './types';
import { askIfAbort } from './utils/askIfAbort';
import { requestInput } from './utils/requestInput';
import { useInternalClient } from './utils/useInternalClient';

type Props = {
  account: Account;
};

function AccountInfo({ account }: Props) {
  const client = useInternalClient();
  const { notify } = useNotifications();

  async function handleDeactivate2FA() {
    if (
      await askIfAbort(
        `Are you sure you want to deactivate 2FA on account ${account.email}?`,
        'Yes, deactivate 2FA',
      )
    ) {
      return;
    }

    try {
      await client.deactivate2Fa(account.id);
      notify(`2FA deactivated for ${account.email}!`, 'success');
    } catch (e) {
      notify('Something went wrong...', 'error');
    }
  }

  async function handleTrashAccount() {
    if (
      await askIfAbort(
        `Are you sure you want to trash account ${account.email}?`,
        'Yes, trash account',
      )
    ) {
      return;
    }

    if (
      await askIfAbort(
        `Sorry for being fussy, but this operation is not free-to-rollback: are you 100% sure? The account that will be trashed is ${account.email}`,
        `I'm really sure`,
      )
    ) {
      return;
    }

    try {
      await client.trashAccount(account.id);
      notify(`Account ${account.email} successfully trashed!`, 'success');
    } catch (e) {
      notify('Something went wrong...', 'error');
    }
  }

  async function handleChangeEmail() {
    try {
      const email = await requestInput(
        `Please insert the new email for account ${account.email}:`,
      );

      if (!email) {
        return;
      }

      await client.changeAccountEmail(account.id, email);
      notify('Email successfully changed!', 'success');
    } catch (e) {
      notify('Something went wrong...', 'error');
    }
  }

  async function handleResetInvitationEmailsLimit() {
    try {
      await client.resetAccountInvitationEmailsBudget(account.id);
      notify(`Invitation emails limit reset for ${account.email}!`, 'success');
    } catch (e) {
      notify('Something went wrong...', 'error');
    }
  }

  const allSites = [
    ...account.sites,
    ...account.organizations.flatMap((o) => o.sites),
  ];

  return (
    <div
      style={{
        border: '1px solid var(--border-color)',
        padding: '15px',
        borderRadius: '5px',
        marginBottom: '10px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid var(--border-color)',
          marginBottom: '15px',
          paddingBottom: '15px',
        }}
      >
        <div style={{ flex: '1' }}>
          <Heading size={VisualSizesEnum.LARGE}>
            {levelEmoji[account.max_support_level]}
            <span style={{ paddingLeft: '10px' }}>{account.email}</span>
          </Heading>
        </div>
        <ActionMenu width={220}>
          <GotoActionMenuItem
            title="Enter dashboard"
            url={account.dashboard_enter_url}
          />

          <ActionMenuItemSpacer />

          <ActionMenuItem iconName="Edit" onClick={handleDeactivate2FA}>
            Deactivate 2FA
          </ActionMenuItem>
          <ActionMenuItem iconName="Edit" onClick={handleChangeEmail}>
            Change email
          </ActionMenuItem>
          <ActionMenuItem
            iconName="CheckmarkCircle"
            onClick={handleResetInvitationEmailsLimit}
          >
            Reset invitation emails limit
          </ActionMenuItem>
          <ActionMenuItem iconName="Trash" onClick={handleTrashAccount}>
            Trash account
          </ActionMenuItem>
          <ActionMenuItemSpacer />

          <CopyActionMenuItem
            title="Account ID"
            isId={true}
            value={account.id}
          />
          {account.name && (
            <CopyActionMenuItem title="Name" value={account.name} />
          )}
          {account.company && (
            <CopyActionMenuItem title="Company" value={account.company} />
          )}
          {account.chargebee_customer_id && (
            <GotoActionMenuItem
              title="Open in Chargebee"
              url={`https://datocms.chargebee.com/customers?view_code=all&Customers.search=${account.chargebee_customer_id!}`}
            />
          )}
          {account.plan && <ActionMenuItemSpacer />}
          {account.plan && (
            <CopyActionMenuItem
              title="Plan ID"
              isId={true}
              value={account.plan.id}
            />
          )}
          {account.plan && (
            <CopyActionMenuItem title="Plan name" value={account.plan.name} />
          )}
          {account.per_owner_pricing_adjuster_ttl && (
            <CopyActionMenuItem
              title="Plan adjusting in"
              value={formatDistanceToNowStrict(
                addSeconds(new Date(), account.per_owner_pricing_adjuster_ttl),
              )}
            />
          )}
          {account.plan && (
            <GotoActionMenuItem
              title="Edit in Sales Tools"
              url={`https://sales-tools.datocms.com/per-owner-pricing-plans/${account.plan?.id}`}
            />
          )}
        </ActionMenu>
      </div>

      <Accordion>
        {allSites.length > 0 && (
          <AccordionSection
            id="sites"
            title={`📚 Projects (${allSites.length})`}
            isOpen={true}
          >
            <Sites account={account} />
          </AccordionSection>
        )}
        {account.organizations.length > 0 && (
          <AccordionSection
            id="organizations"
            title={`🏢 Organizations (${account.organizations.length})`}
          >
            <Organizations account={account} />
          </AccordionSection>
        )}
      </Accordion>

      {allSites.length === 0 && account.organizations.length === 0 && (
        <Paragraph>No projects or organizations on this account!</Paragraph>
      )}
    </div>
  );
}

export default AccountInfo;
